import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CreditCard, Calendar, User, FileText, AlertCircle } from 'lucide-react';
import { SimpleAccountSelector } from '@/app/components/common/SimpleAccountSelector';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { receiveOnAccountPayment, OnAccountPaymentCreate } from '@/app/services/paymentService';
import { Party } from '@/app/types/party';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

interface OnAccountPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  party: Party | undefined;
}

// Account IDs for filtering (specific cash and bank accounts)
const CASH_ACCOUNT_ID = 1;
const BANK_ACCOUNT_ID = 2;
// Add more account IDs as needed
const ADDITIONAL_ACCOUNT_IDS = [204];

// Form validation schema
const paymentSchema = z.object({
  received_in_account_id: z.number({
    required_error: 'Please select an account',
  }),
  payment_amount: z.string()
    .min(1, 'Amount is required')
    .regex(/^-?\d+(\.\d{1,2})?$/, 'Invalid amount format')
    .refine((val) => parseFloat(val) !== 0, 'Amount cannot be zero'),
  transaction_date: z.string().min(1, 'Transaction date is required'),
  reference_number: z.string().optional(),
  narration: z.string().optional(),
});

type PaymentFormData = z.infer<typeof paymentSchema>;

// Helper function to format currency
const formatCurrency = (amount: number | string | undefined | null): string => {
  if (amount === undefined || amount === null) return '₹0.00';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(numAmount);
};

// Detail item component
interface DetailItemProps {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  className?: string;
}

const DetailItem: React.FC<DetailItemProps> = ({ icon: Icon, label, value, className }) => (
  <div className={`flex items-start space-x-2 ${className}`}>
    <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
    <div className="min-w-0 flex-1">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-sm font-medium break-words">{value}</p>
    </div>
  </div>
);

export const OnAccountPaymentModal: React.FC<OnAccountPaymentModalProps> = ({
  isOpen,
  onClose,
  party
}) => {
  const queryClient = useQueryClient();
  
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      transaction_date: format(new Date(), 'yyyy-MM-dd'),
      payment_amount: '',
      reference_number: '',
      narration: '',
    },
  });

  // Create payment mutation
  const createPaymentMutation = useMutation({
    mutationFn: (data: OnAccountPaymentCreate) => receiveOnAccountPayment(data),
    onSuccess: (response) => {
      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['party', party?.id] });
      queryClient.invalidateQueries({ queryKey: ['party-statement', party?.id] });
      queryClient.invalidateQueries({ queryKey: ['party-payments', party?.id] });

      toast.success(`Account adjustment recorded successfully. Receipt #${response.voucher_number}`);
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to record adjustment');
    }
  });

  const onSubmit = (data: PaymentFormData) => {
    if (!party) {
      toast.error('Party information is missing');
      return;
    }

    // Prepare payment data
    const paymentData: OnAccountPaymentCreate = {
      party_id: party.id,
      received_in_account_id: data.received_in_account_id,
      payment_amount: data.payment_amount,
      transaction_date: data.transaction_date,
      reference_number: data.reference_number || undefined,
      narration: data.narration || undefined,
    };

    createPaymentMutation.mutate(paymentData);
  };

  const isSubmitting = createPaymentMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Adjust Account Balance</DialogTitle>
          <DialogDescription>
            Adjust the party's account balance without allocating to specific invoices.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4 overflow-y-auto flex-1">
          {/* Party Details */}
          {party && (
            <div className="bg-muted/50 p-4 rounded-lg space-y-3">
              <h4 className="font-medium text-sm">Party Information</h4>
              <DetailItem icon={User} label="Party Name" value={party.name} />
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Account Selection */}
            <div className="space-y-2">
              <Label htmlFor="receiving-account">
                Account <span className="text-destructive">*</span>
              </Label>
              <Controller
                name="received_in_account_id"
                control={control}
                render={({ field }) => (
                  <SimpleAccountSelector
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select bank or cash account..."
                    filterAccountIds={[CASH_ACCOUNT_ID, BANK_ACCOUNT_ID, ...ADDITIONAL_ACCOUNT_IDS]}
                    disabled={isSubmitting}
                  />
                )}
              />
              {errors.received_in_account_id && (
                <p className="text-sm text-destructive">{errors.received_in_account_id.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Select the bank or cash account for the transaction
              </p>
            </div>

            {/* Adjustment Amount */}
            <div className="space-y-2">
              <Label htmlFor="payment_amount">
                Adjustment Amount <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                <Input
                  id="payment_amount"
                  type="text"
                  placeholder="0.00"
                  {...register('payment_amount')}
                  className="pl-8"
                  disabled={isSubmitting}
                />
              </div>
              {errors.payment_amount && (
                <p className="text-sm text-destructive">{errors.payment_amount.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Positive amount:</span> Payment received from party<br />
                <span className="font-medium">Negative amount:</span> Refund or payment to party
              </p>
            </div>

            {/* Transaction Date */}
            <div className="space-y-2">
              <Label htmlFor="transaction_date">
                Transaction Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="transaction_date"
                type="date"
                {...register('transaction_date')}
                disabled={isSubmitting}
              />
              {errors.transaction_date && (
                <p className="text-sm text-destructive">{errors.transaction_date.message}</p>
              )}
            </div>

            {/* Reference Number */}
            <div className="space-y-2">
              <Label htmlFor="reference_number">Reference Number</Label>
              <Input
                id="reference_number"
                type="text"
                placeholder="Cheque/Transaction number"
                {...register('reference_number')}
                disabled={isSubmitting}
              />
            </div>

            {/* Narration */}
            <div className="space-y-2">
              <Label htmlFor="narration">Notes</Label>
              <Textarea
                id="narration"
                placeholder="Additional notes about this payment"
                {...register('narration')}
                disabled={isSubmitting}
                rows={3}
              />
            </div>
          </div>

          {/* Info Alert */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This adjustment will update the party's account balance. It can be allocated to invoices later.
            </AlertDescription>
          </Alert>
        </form>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Recording Adjustment...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                Record Adjustment
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};